import { CircularProgress, Box } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useEditableProfile } from "../../../hooks/useEditableProfile";

/**
 * Holds a nested profile editor until it has a profile to edit (ECH-387).
 *
 * The editors dereference the profile in their `useState` initialisers, which
 * run before any guard inside the component can. Opened from the Profile UI
 * that is fine, because router state carries a working copy. Reloaded or opened
 * by URL there is no state, and on a cold load the stored profile has not
 * arrived either — `AppContent` dispatches the fetch on mount.
 *
 * Gating at the route rather than inside each of the thirteen editors keeps the
 * fix in one place and avoids the rules-of-hooks problem: a guard that returns
 * before the hooks would change the hook order between renders.
 *
 * A spinner rather than a redirect while loading. The URL is valid and about to
 * work, so bouncing the member would make a shareable link look broken. If the
 * fetch fails, `ProtectedRoute` and the session handling already own that path.
 *
 * ## `requires`: the section, not just the profile
 *
 * Several editors go a level deeper and dereference a role-specific section —
 * expertise, interests, the sponsor record — that the member may not have. A
 * mentee has no expertise; nobody but a sponsor has a `sponsorProfile`; and a
 * member created through the redesigned onboarding has no sub-profiles at all
 * (ECH-385). The Profile UI already hides those sections, so only a typed URL
 * or a reload reaches them.
 *
 * When the section is missing there is genuinely nothing to edit, so this
 * returns to the profile rather than rendering an empty form. That is the
 * "clear fallback instead of a blank frame" ECH-387 asks for. `replace`, so the
 * dead URL does not sit in history waiting for a back press.
 */
const RequireProfileLoaded = ({ children, requires }) => {
  const { profile, isReady } = useEditableProfile();
  const role = profile?.roles?.join("_");

  if (!isReady) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (requires && !requires(profile, role)) {
    return <Navigate to="/profile/edit-profile" replace />;
  }

  return children;
};

export default RequireProfileLoaded;
